/**
 * `AgentReadyPluginDownload` — the recommended WordPress / WooCommerce
 * install path (ADR-0193 §9.4). Downloads the per-site Sill plugin
 * (ADR-0188), which serves BOTH `/.well-known/agent-card.json` AND
 * `/.well-known/sill-proof.txt` and logs AI-crawler traffic server-side.
 *
 * A plugin-only install is therefore COMPLETE — discoverable by every
 * kind of agent (including static/card-lookup bots like ChatGPT), domain
 * verified, and traffic logged — with NO script paste. The `<script>`
 * snippet remains only as the "can't install a plugin" fallback in the
 * composing panel; it is an analytics enhancement, not a requirement
 * (ADR-0193 §2/§9.3).
 *
 * Self-contained: owns its own download plumbing (mirrors the Settings →
 * Integrations "Download WordPress Plugin" affordance) so the onboarding
 * panels don't depend on the large SettingsIntegrations module.
 */

import { Download, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { WORDPRESS_PLUGIN_VERSION } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useSites, useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Collapsible, StepHeading } from '../../components/atoms/index.js';
import { PLUGIN_HOW, PLUGIN_HOW_BODY, PLUGIN_SENTENCE } from './install-copy.js';
import { confirmKeyRotation } from '../settings/install-key-rotation.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
// Opaque marker: when `kicker` begins with "STEP N " the plugin block is
// rendered as a flat step (no outer tinted-lime card). Mono "RECOMMENDED"
// stays boxed — that is the model-presentation lead where the chip IS the
// hierarchy.
const STEP_KICKER_RE = /^STEP\s+\d/i;
// WordPress's Upload Plugin surface accepts ONLY `.zip`; the download
// filename must carry the `.zip` extension so a direct-click download
// lands with the right type.
const FILENAME = 'sill-agent-ready.zip';

function triggerBrowserDownload(
  contents: Blob | string,
  filename: string,
  mime: string,
): void {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return;
  const blob =
    contents instanceof Blob
      ? contents
      : new Blob([contents], { type: mime });
  const object_url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = object_url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(object_url), 0);
}

export interface AgentReadyPluginDownloadProps {
  /** Mono kicker; `RECOMMENDED` in the model presentation, a STEP kicker on the tabs. */
  readonly kicker?: string;
  /** The one sentence above the fold (ADR-0212). */
  readonly sentence?: string;
  /** When `kicker` is a STEP label and this is `false`, the step
   *  heading renders without the numbered badge — the single-step
   *  case (founder 2026-10-21). Default `true` preserves the two-
   *  step layout the WooCommerce tab uses. */
  readonly showStepBadge?: boolean;
}

export function AgentReadyPluginDownload({
  kicker = 'RECOMMENDED',
  sentence = PLUGIN_SENTENCE,
  showStepBadge = true,
}: AgentReadyPluginDownloadProps = {}): JSX.Element | null {
  const C = useC();
  const api = useApi();
  const toast = useToast();
  const { activeSite } = useSites();
  const [downloading, setDownloading] = useState(false);

  if (activeSite === null) return null;
  const site_id = activeSite.site_id;

  const download = async (): Promise<void> => {
    if (downloading) return;
    if (!(await confirmKeyRotation(api.sites, site_id, 'plugin'))) return;
    setDownloading(true);
    try {
      const zip = await api.sites.getAgentReadyWordpressPlugin(site_id);
      triggerBrowserDownload(zip, FILENAME, 'application/zip');
    } catch (err) {
      toast({
        title: 'Could not download the plugin',
        body: err instanceof ApiError ? err.message : 'Unknown error',
        tone: 'coral',
      });
    } finally {
      setDownloading(false);
    }
  };

  const asStep = STEP_KICKER_RE.test(kicker);
  // Preserve the mono kicker text for a non-step kicker (e.g. "RECOMMENDED"
  // in the model-presentation lead). When the caller passed a STEP kicker
  // the new numbered heading replaces it entirely — no mono kicker above.
  return (
    <div
      className={asStep ? 'mb-4' : 'mb-4 p-4'}
      style={asStep ? undefined : {
        border: `1px solid ${C.limeFg}`,
        borderRadius: 2,
        background: C.limeDim,
      }}
      data-testid="wp-plugin-install"
    >
      {asStep ? (
        showStepBadge ? (
          <StepHeading n={1}>Install the Sill plugin</StepHeading>
        ) : (
          <StepHeading>Install the Sill plugin</StepHeading>
        )
      ) : (
        <>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 2,
              color: C.limeFg,
              marginBottom: 8,
            }}
          >
            {kicker}
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              fontWeight: 500,
              color: C.text,
              marginBottom: 4,
            }}
          >
            Install the Sill plugin
          </div>
        </>
      )}
      <p
        style={{
          fontFamily: sansFont,
          fontSize: 14,
          color: C.text,
          lineHeight: 1.55,
          marginBottom: 12,
          maxWidth: '60ch',
        }}
      >
        {sentence}
      </p>
      <button
        type="button"
        onClick={() => void download()}
        disabled={downloading}
        aria-label="Download the Sill plugin"
        className="inline-flex items-center gap-2 px-4 py-2"
        style={{
          background: downloading ? C.surface2 : C.lime,
          color: downloading ? C.muted : C.ink,
          fontFamily: sansFont,
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 2,
          border: 'none',
          cursor: downloading ? 'default' : 'pointer',
        }}
        data-testid="wp-plugin-download"
      >
        {downloading ? (
          <>
            <RefreshCw
              size={12}
              style={{ animation: 'spin 1.4s linear infinite' }}
            />
            Downloading…
          </>
        ) : (
          <>
            <Download size={12} />
            Download the plugin (.zip)
          </>
        )}
      </button>
      <span
        data-testid="wp-plugin-version"
        style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginLeft: 12 }}
      >
        Version {WORDPRESS_PLUGIN_VERSION}
      </span>
      <div style={{ marginTop: 10 }}>
        <Collapsible variant="inline" label={PLUGIN_HOW}>
          <p style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, lineHeight: 1.6, margin: 0, maxWidth: 520 }}>
            {PLUGIN_HOW_BODY}
          </p>
        </Collapsible>
      </div>
    </div>
  );
}
